import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, Send, X, Loader2 } from "lucide-react";
import Api from "../Api/Api.json";

// Inline SVG avatar for "Eva" – a friendly female doctor.
// Long flowing hair, lab coat, draped stethoscope, teal scrubs, lapel cross.
const EvaAvatar = ({ size = 32 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 64 64"
    aria-label="Eva, female doctor avatar"
    role="img"
  >
    <defs>
      <linearGradient id="evaBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#34D399" />
        <stop offset="100%" stopColor="#06B6D4" />
      </linearGradient>
    </defs>

    {/* Background */}
    <circle cx="32" cy="32" r="32" fill="url(#evaBg)" />

    {/* Long hair (back) – flows down past the shoulders */}
    <path
      d="M14 32c0-11 8-19 18-19s18 8 18 19v18c-2 4-5 8-9 11h-18c-4-3-7-7-9-11V32z"
      fill="#3B2A1E"
    />

    {/* Subtle hair highlight */}
    <path
      d="M16 38c1 5 3 9 6 12M48 38c-1 5-3 9-6 12"
      stroke="#5C4632"
      strokeWidth="1"
      strokeLinecap="round"
      fill="none"
      opacity="0.7"
    />

    {/* Earrings */}
    <circle cx="20" cy="36" r="1.2" fill="#FACC15" />
    <circle cx="44" cy="36" r="1.2" fill="#FACC15" />

    {/* Face */}
    <ellipse cx="32" cy="33" rx="9.5" ry="11" fill="#FCE3C8" />

    {/* Bangs / side-swept fringe */}
    <path
      d="M22 27c1.5-4 5.5-7 10-7s8 2 10 5c-2 0-4 1-6 2-2-1-4-1.5-6-1.5-3 0-6 0.5-8 1.5z"
      fill="#3B2A1E"
    />

    {/* Eyebrows */}
    <path
      d="M26 30.5c1.2-0.8 2.8-0.8 4 0M34 30.5c1.2-0.8 2.8-0.8 4 0"
      stroke="#3B2A1E"
      strokeWidth="0.9"
      strokeLinecap="round"
      fill="none"
    />

    {/* Eyelashes */}
    <path
      d="M27 32.2l-0.7-0.6M30 32.2l0.5-0.7M34 32.2l-0.5-0.7M37 32.2l0.7-0.6"
      stroke="#1F2937"
      strokeWidth="0.7"
      strokeLinecap="round"
    />

    {/* Eyes */}
    <ellipse cx="28.5" cy="33" rx="1.2" ry="1.4" fill="#1F2937" />
    <ellipse cx="35.5" cy="33" rx="1.2" ry="1.4" fill="#1F2937" />

    {/* Cheek blush */}
    <ellipse cx="25.5" cy="37" rx="1.6" ry="1" fill="#F4A6A6" opacity="0.55" />
    <ellipse cx="38.5" cy="37" rx="1.6" ry="1" fill="#F4A6A6" opacity="0.55" />

    {/* Lips */}
    <path
      d="M29.5 38.4c1.1 1.2 3.9 1.2 5 0"
      stroke="#BE185D"
      strokeWidth="1.4"
      strokeLinecap="round"
      fill="none"
    />
    <path
      d="M30 38.2c0.8-0.5 3.2-0.5 4 0"
      stroke="#BE185D"
      strokeWidth="0.7"
      strokeLinecap="round"
      fill="none"
      opacity="0.7"
    />

    {/* Neck */}
    <path d="M29 42h6v5h-6z" fill="#FCE3C8" />

    {/* Lab coat */}
    <path
      d="M10 64v-8c0-5 5-9 11-10l11 11 11-11c6 1 11 5 11 10v8z"
      fill="#FFFFFF"
    />

    {/* V-neck scrubs */}
    <path d="M22 46l10 11 10-11-2-1-8 8-8-8z" fill="#0E7490" />

    {/* Lapel seams */}
    <path
      d="M22 46l10 11M42 46l-10 11"
      stroke="#E5E7EB"
      strokeWidth="0.6"
      fill="none"
    />

    {/* Stethoscope - left tube draped over shoulder */}
    <path
      d="M26 46 C 21 50, 21 58, 30 60.5"
      stroke="#0F172A"
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
    />

    {/* Stethoscope - right tube draped over shoulder */}
    <path
      d="M38 46 C 43 50, 43 58, 34 60.5"
      stroke="#0F172A"
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
    />

    {/* Stethoscope chestpiece */}
    <circle
      cx="32"
      cy="60.5"
      r="2.6"
      fill="#CBD5E1"
      stroke="#0F172A"
      strokeWidth="0.8"
    />
    <circle cx="32" cy="60.5" r="1.1" fill="#334155" />

    {/* Medical cross badge on lapel */}
    <rect x="41" y="51" width="5" height="5" rx="0.6" fill="#EF4444" />
    <path
      d="M43.5 52.2v2.6M42.2 53.5h2.6"
      stroke="#FFFFFF"
      strokeWidth="0.9"
      strokeLinecap="round"
    />
  </svg>
);

const getUserId = () => {
  try {
    const raw = localStorage.getItem("user_info");
    if (!raw) return null;
    const info = JSON.parse(raw);
    return info?.di_id || info?.user_id || info?.id || null;
  } catch {
    return null;
  }
};

const SUPPORTED_LANGUAGES = ["english", "bengali"];

const getLanguage = () => {
  try {
    const stored = localStorage.getItem("app_language");
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
  } catch {
    /* noop */
  }
  return "english";
};

const COPY = {
  english: {
    title: "Eva",
    subtitle: "Your health assistant",
    greeting: "Hi, I'm Eva, your health assistant. How are you feeling today?",
    typing: "Eva is typing…",
    placeholder: "Message Eva…",
    placeholderSending: "Please wait…",
    placeholderLocked: "Chat is unavailable",
    locked: "Your chat session has ended. Please come back later.",
    sessionStartFailed:
      "Sorry, I couldn't start our chat right now. Please try again later.",
    genericError: "Sorry, something went wrong. Please try again.",
    fallbackReply: "I'm not sure how to respond to that.",
    languageLabel: "Language",
  },
  bengali: {
    title: "ইভা",
    subtitle: "আপনার স্বাস্থ্য সহকারী",
    greeting: "হ্যালো, আমি ইভা, আপনার স্বাস্থ্য সহকারী। আপনি আজ কেমন অনুভব করছেন?",
    typing: "ইভা টাইপ করছে…",
    placeholder: "ইভাকে লিখুন…",
    placeholderSending: "অনুগ্রহ করে অপেক্ষা করুন…",
    placeholderLocked: "চ্যাট উপলব্ধ নয়",
    locked: "আপনার চ্যাট সেশন শেষ হয়েছে। অনুগ্রহ করে পরে আবার আসুন।",
    sessionStartFailed:
      "দুঃখিত, এখন চ্যাট শুরু করা যাচ্ছে না। অনুগ্রহ করে পরে চেষ্টা করুন।",
    genericError: "দুঃখিত, কিছু একটা সমস্যা হয়েছে। আবার চেষ্টা করুন।",
    fallbackReply: "আমি নিশ্চিত নই কীভাবে উত্তর দেব।",
    languageLabel: "ভাষা",
  },
};

const FloatingChatButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isLocked, setIsLocked] = useState(false); // becomes true on code 401
  const [hasStartedSession, setHasStartedSession] = useState(false);
  const [language, setLanguage] = useState(getLanguage());
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const userId = getUserId();
  const t = COPY[language] || COPY.english;

  const handleLanguageChange = (next) => {
    if (!SUPPORTED_LANGUAGES.includes(next) || next === language) return;
    setLanguage(next);
    try {
      localStorage.setItem("app_language", next);
    } catch {
      /* noop */
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      // small delay so the modal animates in before scroll
      setTimeout(scrollToBottom, 50);
    }
  }, [isOpen, messages]);

  const callChatApi = async ({ message, startingSession }) => {
    const url = Api.RECOMMENDATIONS_TEXT_API;
    const headers = { headers: { "Content-Type": "application/json" } };
    const body = {
      task: "chatInApp",
      startingSession: startingSession ? "True" : "False",
      app: "energyband",
      user_id: userId,
      language,
      message,
    };
    const res = await axios.post(url, body, headers);
    return res?.data || {};
  };

  const handleOpen = async () => {
    setIsOpen(true);
    if (hasStartedSession) return;

    // Greet the user locally for instant feedback
    setMessages((prev) =>
      prev.length
        ? prev
        : [
            {
              id: `eva-greet-${Date.now()}`,
              sender: "eva",
              text: t.greeting,
            },
          ]
    );

    // Open session against the backend
    setIsSending(true);
    try {
      const data = await callChatApi({
        message: "Session start.",
        startingSession: true,
      });
      setHasStartedSession(true);

      const replyText =
        typeof data?.msg === "string"
          ? data.msg
          : data?.msg
          ? JSON.stringify(data.msg)
          : "";

      if (replyText) {
        setMessages((prev) => [
          ...prev,
          { id: `eva-${Date.now()}`, sender: "eva", text: replyText },
        ]);
      }

      if (data?.code === 401) {
        setIsLocked(true);
      }
    } catch (err) {
      console.error("Eva session start failed:", err);
      setMessages((prev) => [
        ...prev,
        {
          id: `eva-err-${Date.now()}`,
          sender: "eva",
          text: t.sessionStartFailed,
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const trimmed = input.trim();
    if (!trimmed || isSending || isLocked) return;

    const userMsg = {
      id: `user-${Date.now()}`,
      sender: "user",
      text: trimmed,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsSending(true);

    try {
      const data = await callChatApi({
        message: trimmed,
        startingSession: !hasStartedSession,
      });
      if (!hasStartedSession) setHasStartedSession(true);

      const replyText =
        typeof data?.msg === "string"
          ? data.msg
          : data?.msg
          ? JSON.stringify(data.msg)
          : t.fallbackReply;

      setMessages((prev) => [
        ...prev,
        { id: `eva-${Date.now()}`, sender: "eva", text: replyText },
      ]);

      if (data?.code === 401) {
        setIsLocked(true);
      }
    } catch (err) {
      console.error("Eva chat error:", err);
      setMessages((prev) => [
        ...prev,
        {
          id: `eva-err-${Date.now()}`,
          sender: "eva",
          text: t.genericError,
        },
      ]);
    } finally {
      setIsSending(false);
      // refocus the input for fast follow-up typing
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <>
      {/* Floating launcher button */}
      {!isOpen && (
        <button
          type="button"
          onClick={handleOpen}
          aria-label="Open chat with Eva"
          className="fixed right-4 z-[60] flex items-center justify-center w-14 h-14 rounded-full shadow-lg bg-gradient-to-br from-emerald-400 to-cyan-500 text-white hover:scale-105 active:scale-95 transition-transform"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)" }}
        >
          <MessageCircle className="w-7 h-7" />
          <span className="absolute -top-1 -right-1 bg-white text-emerald-600 text-[10px] font-bold rounded-full px-1.5 py-0.5 shadow">
            Eva
          </span>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center sm:justify-end bg-black/40"
          onClick={handleClose}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:w-[400px] sm:mr-4 sm:mb-4 bg-[#0D1117] border border-[#30363D] sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col overflow-hidden"
            style={{
              maxHeight: "85vh",
              height: "85vh",
            }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white">
              <EvaAvatar size={40} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold leading-tight truncate">
                  {t.title}
                </div>
                <div className="text-xs opacity-90 truncate">{t.subtitle}</div>
              </div>

              {/* Language toggle */}
              <div
                role="group"
                aria-label={t.languageLabel}
                className="flex items-center bg-white/20 rounded-full p-0.5 text-xs font-semibold backdrop-blur"
              >
                <button
                  type="button"
                  onClick={() => handleLanguageChange("english")}
                  aria-pressed={language === "english"}
                  className={`px-2.5 py-1 rounded-full transition-colors ${
                    language === "english"
                      ? "bg-white text-emerald-600"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => handleLanguageChange("bengali")}
                  aria-pressed={language === "bengali"}
                  className={`px-2.5 py-1 rounded-full transition-colors ${
                    language === "bengali"
                      ? "bg-white text-emerald-600"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  বাং
                </button>
              </div>

              <button
                type="button"
                onClick={handleClose}
                aria-label="Close chat"
                className="p-1 rounded-full hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-[#0D1117]">
              {messages.map((m) =>
                m.sender === "user" ? (
                  <div key={m.id} className="flex justify-end">
                    <div className="max-w-[78%] px-3 py-2 rounded-2xl rounded-br-sm bg-[#1F6FEB] text-white text-sm whitespace-pre-wrap break-words">
                      {m.text}
                    </div>
                  </div>
                ) : (
                  <div key={m.id} className="flex items-end gap-2">
                    <EvaAvatar size={28} />
                    <div className="max-w-[78%] px-3 py-2 rounded-2xl rounded-bl-sm bg-[#161B22] border border-[#30363D] text-[#F0F6FC] text-sm whitespace-pre-wrap break-words">
                      {m.text}
                    </div>
                  </div>
                )
              )}

              {isSending && (
                <div className="flex items-end gap-2">
                  <EvaAvatar size={28} />
                  <div className="px-3 py-2 rounded-2xl rounded-bl-sm bg-[#161B22] border border-[#30363D] text-[#9CA3AF] text-sm flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t.typing}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Locked banner */}
            {isLocked && (
              <div className="px-4 py-2 text-xs text-red-200 bg-red-900/40 border-t border-red-700">
                {t.locked}
              </div>
            )}

            {/* Composer */}
            <form
              onSubmit={handleSend}
              className="flex items-center gap-2 p-3 border-t border-[#30363D] bg-[#0D1117]"
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  isLocked
                    ? t.placeholderLocked
                    : isSending
                    ? t.placeholderSending
                    : t.placeholder
                }
                disabled={isLocked || isSending}
                className="flex-1 px-3 py-2 rounded-full bg-[#161B22] border border-[#30363D] text-[#F0F6FC] placeholder-[#6E7681] outline-none focus:border-[#58A6FF] disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={isLocked || isSending || !input.trim()}
                aria-label="Send message"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingChatButton;
